import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { toast } from "react-toastify";
import {
    FaPlus, FaSearch, FaTimes, FaMapMarkerAlt, FaArrowLeft, FaArrowRight,
    FaShoppingCart, FaRocket, FaBolt, FaStore, FaCheck, FaTag, FaBox,
    FaTruck, FaMoneyBillWave, FaPercent, FaPhone, FaHome, FaCity,
    FaEnvelope, FaDirections, FaCheckCircle, FaExclamationTriangle,
    FaSpinner, FaTrash, FaImage, FaInfoCircle, FaWifi
} from "react-icons/fa";
import {
    getProducts,
    fetchBranchesData,
    getInventoryDataForBranch,
    getCoupons,
    getShiprocketCourierRates,
    createCustomOrder,
    validateCustomOrderBranch,
    getShiprocketSettings
} from "../../services/ClientService";

// Resolve product image URL — images are stored as { preview, ... } objects
const getProductImageUrl = (product) => {
    if (product?.images?.[0]?.preview) return product.images[0].preview;
    const firstOption = product?.productOptions?.[0];
    if (firstOption?.images?.[0]?.preview) return firstOption.images[0].preview;
    return null;
};

const getProductImageData = (product) => {
    if (product?.images?.[0]) return product.images[0];
    const firstOption = product?.productOptions?.[0];
    if (firstOption?.images?.[0]) return firstOption.images[0];
    return null;
};

// Product image with graceful fallback
const ProductImage = ({ src, alt, className }) => {
    const [err, setErr] = useState(false);
    if (!src || err) {
        return (
            <div className={`${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center flex-shrink-0`}>
                <FaImage className="text-gray-300 dark:text-gray-600" size="1.1em" />
            </div>
        );
    }
    return <img src={src} alt={alt || ""} className={`${className} object-cover flex-shrink-0`} onError={() => setErr(true)} />;
};

const libraries = ["places"];

const ORDER_TYPES = [
    {
        id: "regular_online",
        label: "Regular Online Order",
        description: "Manual delivery charges. Standard online order flow.",
        icon: <FaShoppingCart size="1.5em" />,
        color: "blue"
    },
    {
        id: "regular_shiprocket",
        label: "Regular Shiprocket Order",
        description: "Shiprocket delivery options. Ship Now available in orders.",
        icon: <FaRocket size="1.5em" />,
        color: "purple"
    },
    {
        id: "quick_shiprocket",
        label: "Quick Shiprocket Order",
        description: "Hyperlocal/same-day Shiprocket delivery. Quick Ship Now.",
        icon: <FaBolt size="1.5em" />,
        color: "orange"
    }
];

const STEPS = [
    { id: 1, label: "Order Type", icon: <FaTag /> },
    { id: 2, label: "Product", icon: <FaBox /> },
    { id: 3, label: "Address", icon: <FaMapMarkerAlt /> },
    { id: 4, label: "Branch", icon: <FaStore /> },
    { id: 5, label: "Delivery & Payment", icon: <FaTruck /> },
    { id: 6, label: "Review & Create", icon: <FaCheckCircle /> }
];

const CreateCustomOrder = () => {
    const navigate = useNavigate();
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyBE4x7mA5JreDo4WjA9TvWpESAzLB4Kf_U",
        libraries,
    });
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const addressInputRef = useRef(null);
    const suppressAddressSearch = useRef(false); // prevents re-search after suggestion selection

    // Wizard state
    const [currentStep, setCurrentStep] = useState(0); // 0 = landing page with Create Order button
    const [showWizard, setShowWizard] = useState(false);

    // Step 1: Order Type
    const [selectedOrderType, setSelectedOrderType] = useState(null);

    // Step 2: Product Selection
    const [productSearch, setProductSearch] = useState("");
    const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
    const [productSearchResults, setProductSearchResults] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]); // Array of { product, selectedOption, quantity }
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const PRODUCT_SEARCH_LIMIT = 100;

    // Step 3: Address
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [addressSearch, setAddressSearch] = useState("");
    const [address, setAddress] = useState({
        name: "",
        phone: "",
        house: "",
        area: "",
        landmark: "",
        address: "",  // full Google geocoded address string from Search Location
        city: "",
        pincode: "",
        state: "",
        directions: "",
        location: null
    });
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

    // Step 4: Branch
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchValidation, setBranchValidation] = useState(null);
    const [validatingBranch, setValidatingBranch] = useState(false);

    // Step 5: Delivery & Coupon
    const [paymentMethod, setPaymentMethod] = useState("cod"); // 'cod' or 'prepaid'
    const [deliveryCharges, setDeliveryCharges] = useState(0);
    const [customCouponAmount, setCustomCouponAmount] = useState(0);
    const [couponApplied, setCouponApplied] = useState(false);
    const [shiprocketCouriers, setShiprocketCouriers] = useState([]);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [loadingCouriers, setLoadingCouriers] = useState(false);
    const [hyperlocalAvailable, setHyperlocalAvailable] = useState(null); // null=unchecked, true/false=checked

    // Step 6: Review & Create
    const [creatingOrder, setCreatingOrder] = useState(false);

    // Initialize Google Places
    useEffect(() => {
        if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            // Create a hidden div for PlacesService
            const div = document.createElement("div");
            placesService.current = new window.google.maps.places.PlacesService(div);
        }
    }, [isLoaded]);

    // Debounce product search input before hitting the API
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedProductSearch(productSearch.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [productSearch]);

    // Server-side product search — searches full catalog, not just the first page
    useEffect(() => {
        if (!debouncedProductSearch) {
            setProductSearchResults([]);
            setShowProductDropdown(false);
            return;
        }

        let cancelled = false;

        const searchProducts = async () => {
            setLoadingProducts(true);
            setShowProductDropdown(true);
            try {
                const res = await getProducts({
                    searchTerm: debouncedProductSearch,
                    skip: 0,
                    limit: PRODUCT_SEARCH_LIMIT,
                    isActive: [true, false],
                });
                if (cancelled) return;
                if (res.success && res.data) {
                    setProductSearchResults(res.data);
                } else {
                    setProductSearchResults([]);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Error searching products:", err);
                    setProductSearchResults([]);
                }
            } finally {
                if (!cancelled) setLoadingProducts(false);
            }
        };

        searchProducts();
        return () => {
            cancelled = true;
        };
    }, [debouncedProductSearch]);

    // Google Places address search
    const searchAddress = useCallback((input) => {
        if (!autocompleteService.current || !input || input.length < 3) {
            setAddressSuggestions([]);
            setShowAddressSuggestions(false);
            return;
        }
        autocompleteService.current.getPlacePredictions(
            {
                input,
                componentRestrictions: { country: "in" }
            },
            (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    setAddressSuggestions(predictions);
                    setShowAddressSuggestions(true);
                } else {
                    setAddressSuggestions([]);
                    setShowAddressSuggestions(false);
                }
            }
        );
    }, []);

    useEffect(() => {
        if (suppressAddressSearch.current) {
            suppressAddressSearch.current = false;
            return;
        }
        const timer = setTimeout(() => {
            if (addressSearch.length >= 3) {
                searchAddress(addressSearch);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [addressSearch, searchAddress]);

    const selectAddressSuggestion = (suggestion) => {
        if (!placesService.current) return;
        placesService.current.getDetails(
            { placeId: suggestion.place_id, fields: ["geometry", "address_components", "formatted_address"] },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                    const components = place.address_components || [];
                    let city = "", state = "", pincode = "", area = "";

                    for (const comp of components) {
                        if (comp.types.includes("locality")) city = comp.long_name;
                        if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
                        if (comp.types.includes("postal_code")) pincode = comp.long_name;
                        if (comp.types.includes("sublocality_level_1") || comp.types.includes("sublocality")) {
                            area = comp.long_name;
                        }
                    }

                    // Use suggestion.description (the autocomplete display text e.g. "Indra Meadows, behind Bank of Baroda...")
                    // NOT place.formatted_address — Google geocodes that to a street number ("1-98/5/17&18...")
                    // which loses the human-readable location name. Mobile app does the same.
                    const displayAddress = suggestion.description;

                    setAddress(prev => ({
                        ...prev,
                        area: area || prev.area,
                        city: city || prev.city,
                        state: state || prev.state,
                        pincode: pincode || prev.pincode,
                        address: displayAddress,  // location-name based address matching mobile app behaviour
                        location: place.geometry?.location ? {
                            type: "Point",
                            coordinates: [
                                place.geometry.location.lng(),
                                place.geometry.location.lat()
                            ]
                        } : null
                    }));
                    suppressAddressSearch.current = true;
                    setAddressSearch(displayAddress);
                    setAddressSuggestions([]);
                }
                setShowAddressSuggestions(false);
            }
        );
    };

    // Select a product and set default option
    const addProduct = (product) => {
        // Check if already added
        if (selectedItems.find(item => item.product._id === product._id)) {
            toast.warning("This product is already added");
            return;
        }

        const defaultOption = product.productOptions?.[0] || null;
        setSelectedItems(prev => [...prev, {
            product,
            selectedOption: defaultOption,
            quantity: 1
        }]);
        setProductSearch("");
        setShowProductDropdown(false);
    };

    const removeProduct = (index) => {
        setSelectedItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateItemOption = (index, option) => {
        setSelectedItems(prev => prev.map((item, i) =>
            i === index ? { ...item, selectedOption: option } : item
        ));
    };

    const updateItemQuantity = (index, qty) => {
        const quantity = Math.max(1, parseInt(qty) || 1);
        setSelectedItems(prev => prev.map((item, i) =>
            i === index ? { ...item, quantity } : item
        ));
    };

    // Fetch branches
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const res = await fetchBranchesData();
                if (res.success && res.data) {
                    setBranches(Array.isArray(res.data) ? res.data : [res.data]);
                }
            } catch (err) {
                console.error("Error fetching branches:", err);
            }
        };
        fetchBranches();
    }, []);

    // Validate branch when selected
    const handleBranchSelect = async (branch) => {
        setSelectedBranch(branch);
        setBranchValidation(null);
        setValidatingBranch(true);

        try {
            const itemsToValidate = selectedItems.map(item => ({
                productId: item.product._id,
                productName: item.product.productName,
                selectedOptionId: item.selectedOption?._id,
                quantity: item.quantity,
                commonInventoryType: item.product.commonInventoryType || false,
                selectedWeight: item.selectedOption ? { weight: item.selectedOption.weight } : null,
                weightPerUnit: item.selectedOption?.weight?.weightValue || 0
            }));

            const res = await validateCustomOrderBranch({
                branchId: branch._id,
                items: itemsToValidate,
                deliveryPincode: address.pincode || null,
                deliveryCoordinates: address.location?.coordinates || null
            });

            setBranchValidation(res.data || res);
        } catch (err) {
            const errorMsg = err.errorData?.message || err.message || "Failed to validate branch";
            setBranchValidation({
                allAvailable: false,
                unavailableItems: [{ reason: errorMsg }],
                availableItems: [],
                isDeliverable: false,
                deliveryMessage: errorMsg
            });
        } finally {
            setValidatingBranch(false);
        }
    };

    // Fetch Shiprocket courier rates
    const fetchCourierRates = async () => {
        if (!selectedBranch || !address.pincode) return;

        setLoadingCouriers(true);
        setHyperlocalAvailable(null);
        try {
            // Calculate total weight
            let totalWeight = 0;
            for (const item of selectedItems) {
                const weightVal = item.selectedOption?.weight?.weightValue || 0.5;
                totalWeight += weightVal * item.quantity;
            }
            // Convert to KG, minimum 0.5
            totalWeight = Math.max(totalWeight / 1000, 0.5);
            if (totalWeight < 0.001) totalWeight = 0.5;

            const pickupPincode = selectedBranch.branchPincode;
            const deliveryPincode = address.pincode;

            // Pass actual delivery coordinates for accurate hyperlocal serviceability check
            // address.location is GeoJSON Point: { type: "Point", coordinates: [lng, lat] }
            const deliveryCoords = address.location?.coordinates;
            const deliveryLat = deliveryCoords ? deliveryCoords[1] : null;
            const deliveryLng = deliveryCoords ? deliveryCoords[0] : null;

            const res = await getShiprocketCourierRates({
                pickupPincode,
                deliveryPincode,
                weight: totalWeight,
                cod: paymentMethod === 'cod' ? 1 : 0,
                ...(deliveryLat && deliveryLng ? { deliveryLat, deliveryLng } : {})
            });

            if (res.success && res.data?.couriers) {
                let couriers = res.data.couriers;
                const hlAvailable = res.data.hyperlocalAvailable || false;

                // For quick_shiprocket: show ONLY hyperlocal couriers
                if (selectedOrderType === "quick_shiprocket") {
                    const hyperlocal = couriers.filter(c => c.mode === "Hyperlocal" || c.is_hyperlocal);
                    couriers = hyperlocal; // empty if not available
                    setHyperlocalAvailable(hyperlocal.length > 0);
                } else {
                    setHyperlocalAvailable(hlAvailable);
                }

                setShiprocketCouriers(couriers);
            }
        } catch (err) {
            console.error("Error fetching courier rates:", err);
            toast.error("Failed to fetch delivery rates");
        } finally {
            setLoadingCouriers(false);
        }
    };

    // Fetch couriers when on delivery step for shiprocket orders, or when payment method changes
    useEffect(() => {
        if (currentStep === 5 && (selectedOrderType === "regular_shiprocket" || selectedOrderType === "quick_shiprocket")) {
            setSelectedCourier(null);
            fetchCourierRates();
        }
    }, [currentStep, paymentMethod]);

    // Calculate financial summary
    const calculateFinancials = () => {
        let subtotal = 0;
        let totalDiscount = 0;

        for (const item of selectedItems) {
            const opt = item.selectedOption;
            if (!opt) continue;
            const price = opt.discountPrice || opt.originalPrice || 0;
            const originalPrice = opt.originalPrice || 0;
            subtotal += price * item.quantity;
            totalDiscount += (originalPrice - price) * item.quantity;
        }

        const couponDiscount = couponApplied ? (customCouponAmount || 0) : 0;
        const delCharges = selectedCourier ? (selectedCourier.rate || selectedCourier.freight_charge || 0) : (deliveryCharges || 0);
        const finalAmount = Math.max(0, subtotal - couponDiscount + delCharges);

        return {
            subtotal: Math.round(subtotal * 100) / 100,
            totalOriginalPrice: Math.round((subtotal + totalDiscount) * 100) / 100,
            totalDiscount: Math.round(totalDiscount * 100) / 100,
            couponDiscount: Math.round(couponDiscount * 100) / 100,
            deliveryCharges: Math.round(delCharges * 100) / 100,
            totalGstAmount: 0,
            finalAmount: Math.round(finalAmount * 100) / 100
        };
    };

    // Step validation
    const canProceed = () => {
        switch (currentStep) {
            case 1: return !!selectedOrderType;
            case 2: return selectedItems.length > 0 && selectedItems.every(i => i.selectedOption && i.quantity > 0);
            case 3: return address.phone && address.pincode && address.city;
            case 4: return selectedBranch && branchValidation?.allAvailable;
            case 5: {
                if (selectedOrderType === "regular_online") {
                    return true; // Delivery charges can be 0
                }
                return !!selectedCourier;
            }
            case 6: return true;
            default: return false;
        }
    };

    // Create order
    const handleCreateOrder = async () => {
        setCreatingOrder(true);
        try {
            const financials = calculateFinancials();
            const orderItems = selectedItems.map(item => ({
                productId: item.product._id,
                productName: item.product.productName,
                description: item.product.description || "",
                images: getProductImageData(item.product),
                quantity: item.quantity,
                commonInventoryType: item.product.commonInventoryType || false,
                selectedOptionId: item.selectedOption?._id,
                selectedWeight: item.selectedOption ? { weight: item.selectedOption.weight } : null,
                weightPerUnit: item.selectedOption?.weight?.weightValue || 0,
                optionLabel: typeof item.selectedOption?.weight === "object"
                    ? item.selectedOption.weight.weightLabel
                    : item.selectedOption?.weight || "",
                pricing: {
                    originalPrice: item.selectedOption?.originalPrice || 0,
                    discountPrice: item.selectedOption?.discountPrice || item.selectedOption?.originalPrice || 0
                },
                finalPricing: {
                    originalPrice: item.selectedOption?.originalPrice || 0,
                    discountPrice: item.selectedOption?.discountPrice || item.selectedOption?.originalPrice || 0
                },
                productOptions: item.product.productOptions || [],
                categoryInfo: item.product.categoryInfo || {},
                gstDetails: item.product.gstDetails || {}
            }));

            const deliveryOpt = selectedCourier ? {
                source: "shiprocket",
                courier_company_id: selectedCourier.courier_company_id,
                service: selectedCourier.courier_name,
                courier_name: selectedCourier.courier_name,
                rate: selectedCourier.rate || selectedCourier.freight_charge || 0,
                isHyperlocal: selectedOrderType === "quick_shiprocket",
                shipping_method: selectedOrderType === "quick_shiprocket" ? "HL" : "",
                etd: selectedCourier.etd || "",
                type: selectedCourier.mode || ""
            } : null;

            const payload = {
                orderType: selectedOrderType,
                items: orderItems,
                branchId: selectedBranch._id,
                address: {
                    name: address.name,
                    phone: address.phone,
                    house: address.house,
                    area: address.area,
                    landmark: address.landmark,
                    address: address.address,  // full Google geocoded string from Search Location
                    city: address.city,
                    pincode: address.pincode,
                    state: address.state,
                    directions: address.directions,
                    location: address.location
                },
                financial: financials,
                deliveryOption: deliveryOpt,
                coupon: couponApplied && customCouponAmount > 0 ? {
                    code: "CUSTOM_ADMIN",
                    discountAmount: customCouponAmount,
                    discountType: "fixed",
                    discountValue: customCouponAmount,
                    isCustom: true
                } : null,
                mobileNumber: address.phone,
                paymentMethod: paymentMethod
            };

            const res = await createCustomOrder(payload);
            if (res.success) {
                toast.success(`${res.data?.orderLabel || "Custom order"} created successfully! Order ID: ${res.data?.orderId}`);
                navigate("/orders");
            } else {
                toast.error(res.message || "Failed to create order");
            }
        } catch (err) {
            const errorMsg = err.errorData?.message || err.message || "Failed to create order";
            toast.error(errorMsg);
        } finally {
            setCreatingOrder(false);
        }
    };

    // Stepper navigation
    const goNext = () => {
        if (canProceed()) {
            setCurrentStep(prev => Math.min(prev + 1, 6));
        }
    };

    const goBack = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    // ==========================================
    // RENDER
    // ==========================================

    // Landing page (before wizard starts)
    if (!showWizard) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-900 p-4 md:p-8">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white mb-2">
                            Create Custom Order
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400">
                            Manually create an order on behalf of a customer with custom pricing and delivery options.
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-6">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                                <FaShoppingCart className="text-blue-600 dark:text-blue-400" size="1.2em" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">6-Step Order Wizard</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Follow the guided steps to create a complete order with product selection, delivery address, branch fulfillment, and payment details.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                            {STEPS.map((step, idx) => (
                                <div key={step.id} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{step.label}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => { setShowWizard(true); setCurrentStep(1); }}
                            className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-base font-semibold"
                        >
                            <FaPlus size="1em" />
                            Start Creating Order
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Step progress bar
    const renderStepper = () => (
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm px-3 py-4">
            {/* Icon row — each step owns its flex-1 slot so lines always pierce icon centers */}
            <div className="flex items-center">
                {STEPS.map((step, idx) => {
                    const stepNum = idx + 1;
                    const isActive = currentStep === stepNum;
                    const isCompleted = currentStep > stepNum;
                    const isFirst = idx === 0;
                    const isLast = idx === STEPS.length - 1;
                    return (
                        <div key={step.id} className="flex-1 flex items-center">
                            {/* Left connector */}
                            <div className={`flex-1 h-[2px] rounded-full transition-all duration-500 ${
                                isFirst ? "invisible" :
                                currentStep >= stepNum ? "bg-emerald-400" : "bg-gray-200 dark:bg-gray-700"
                            }`} />
                            {/* Step circle */}
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm transition-all duration-300 ${
                                isCompleted
                                    ? "bg-emerald-500 text-white shadow-md shadow-emerald-200 dark:shadow-emerald-900/50"
                                    : isActive
                                    ? "bg-blue-600 text-white shadow-md shadow-blue-200 dark:shadow-blue-900/50 ring-4 ring-blue-100 dark:ring-blue-900/40"
                                    : "bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500"
                            }`}>
                                {isCompleted ? <FaCheck size="0.75em" /> : step.icon}
                            </div>
                            {/* Right connector */}
                            <div className={`flex-1 h-[2px] rounded-full transition-all duration-500 ${
                                isLast ? "invisible" :
                                currentStep > stepNum ? "bg-emerald-400" : "bg-gray-200 dark:bg-gray-700"
                            }`} />
                        </div>
                    );
                })}
            </div>
            {/* Label row — same flex-1 structure, labels centered under every icon */}
            <div className="flex items-start mt-2.5">
                {STEPS.map((step, idx) => {
                    const stepNum = idx + 1;
                    const isActive = currentStep === stepNum;
                    const isCompleted = currentStep > stepNum;
                    return (
                        <div key={step.id} className="flex-1 flex justify-center px-0.5">
                            <span className={`text-center leading-tight font-semibold transition-colors duration-300 text-[9px] sm:text-[11px] ${
                                isActive ? "text-blue-600 dark:text-blue-400" :
                                isCompleted ? "text-emerald-600 dark:text-emerald-400" :
                                "text-gray-400 dark:text-gray-500"
                            }`}>
                                {step.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // Step 1: Order Type Selection
    const renderStep1 = () => (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Select Order Type</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose how this order will be delivered to the customer.</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
                {ORDER_TYPES.map(type => {
                    const isSelected = selectedOrderType === type.id;
                    const styles = {
                        blue: {
                            card: isSelected ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 hover:border-blue-300 bg-white dark:bg-gray-800",
                            icon: isSelected ? "bg-blue-600 text-white" : "bg-blue-100 dark:bg-blue-900/30 text-blue-500",
                            title: isSelected ? "text-blue-700 dark:text-blue-300" : "text-gray-800 dark:text-white",
                            badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                        },
                        purple: {
                            card: isSelected ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : "border-gray-200 dark:border-gray-700 hover:border-purple-300 bg-white dark:bg-gray-800",
                            icon: isSelected ? "bg-purple-600 text-white" : "bg-purple-100 dark:bg-purple-900/30 text-purple-500",
                            title: isSelected ? "text-purple-700 dark:text-purple-300" : "text-gray-800 dark:text-white",
                            badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
                        },
                        orange: {
                            card: isSelected ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20" : "border-gray-200 dark:border-gray-700 hover:border-orange-300 bg-white dark:bg-gray-800",
                            icon: isSelected ? "bg-orange-600 text-white" : "bg-orange-100 dark:bg-orange-900/30 text-orange-500",
                            title: isSelected ? "text-orange-700 dark:text-orange-300" : "text-gray-800 dark:text-white",
                            badge: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300"
                        }
                    };
                    const s = styles[type.color];
                    return (
                        <button
                            key={type.id}
                            onClick={() => setSelectedOrderType(type.id)}
                            className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${s.card}`}
                        >
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${s.icon}`}>
                                {type.icon}
                            </div>
                            <div className="flex-1">
                                <h3 className={`font-semibold mb-0.5 transition-colors ${s.title}`}>{type.label}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">{type.description}</p>
                            </div>
                            {isSelected && (
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${s.icon}`}>
                                    <FaCheck size="0.65em" />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    // Step 2: Product Selection
    const renderStep2 = () => (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Select Products</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Search and add products to this order.</p>
            </div>

            {/* Product search */}
            <div className="relative mb-5">
                <div className="flex items-center bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 focus-within:border-blue-500 transition-colors">
                    {loadingProducts ? (
                        <FaSpinner className="text-gray-400 mr-3 animate-spin flex-shrink-0" />
                    ) : (
                        <FaSearch className="text-gray-400 mr-3 flex-shrink-0" />
                    )}
                    <input
                        type="text"
                        placeholder="Search products by name..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-gray-800 dark:text-white placeholder-gray-400 text-sm"
                    />
                    {productSearch && (
                        <button onClick={() => { setProductSearch(""); setShowProductDropdown(false); }} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
                            <FaTimes size="0.85em" />
                        </button>
                    )}
                </div>

                {/* Search results dropdown */}
                {showProductDropdown && loadingProducts && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-6 text-center">
                        <FaSpinner className="text-blue-500 mx-auto mb-2 animate-spin" size="1.25em" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Searching products...</p>
                    </div>
                )}
                {showProductDropdown && !loadingProducts && productSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
                        {productSearchResults.length === PRODUCT_SEARCH_LIMIT && (
                            <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/30">
                                Showing first {PRODUCT_SEARCH_LIMIT} matches — refine your search for more specific results.
                            </p>
                        )}
                        {productSearchResults.map(product => (
                            <button
                                key={product._id}
                                onClick={() => addProduct(product)}
                                className="w-full p-3 text-left hover:bg-blue-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 flex items-center gap-3 transition-colors"
                            >
                                <ProductImage
                                    src={getProductImageUrl(product)}
                                    alt={product.productName}
                                    className="w-11 h-11 rounded-lg"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-800 dark:text-white truncate text-sm">{product.productName}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {product.productOptions?.length || 0} option{product.productOptions?.length !== 1 ? "s" : ""}
                                        {product.commonInventoryType && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 rounded text-[10px] font-medium">Common Inventory</span>}
                                    </p>
                                </div>
                                <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                    <FaPlus className="text-blue-600 dark:text-blue-400" size="0.6em" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                {showProductDropdown && !loadingProducts && productSearch && productSearchResults.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-6 text-center">
                        <FaSearch className="text-gray-300 mx-auto mb-2" size="1.5em" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">No products found for "{productSearch}"</p>
                    </div>
                )}
            </div>

            {/* Selected items */}
            {selectedItems.length === 0 ? (
                <div className="text-center py-14 text-gray-400 dark:text-gray-600">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FaBox size="1.5em" className="opacity-50" />
                    </div>
                    <p className="font-medium text-gray-500 dark:text-gray-400">No products added yet</p>
                    <p className="text-sm mt-1">Search and add products above to continue</p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">{selectedItems.length} Product{selectedItems.length > 1 ? "s" : ""} Added</p>
                    {selectedItems.map((item, idx) => (
                        <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                                <ProductImage
                                    src={getProductImageUrl(item.product)}
                                    alt={item.product.productName}
                                    className="w-16 h-16 rounded-xl"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <h4 className="font-semibold text-gray-800 dark:text-white text-sm leading-snug">{item.product.productName}</h4>
                                        <button
                                            onClick={() => removeProduct(idx)}
                                            className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            title="Remove"
                                        >
                                            <FaTrash size="0.75em" />
                                        </button>
                                    </div>

                                    {/* Option selector */}
                                    {item.product.productOptions && item.product.productOptions.length > 0 && (
                                        <div className="mt-2">
                                            <select
                                                value={item.selectedOption?._id || ""}
                                                onChange={(e) => {
                                                    const opt = item.product.productOptions.find(o => o._id === e.target.value);
                                                    updateItemOption(idx, opt);
                                                }}
                                                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-400"
                                            >
                                                {item.product.productOptions.map(opt => (
                                                    <option key={opt._id} value={opt._id}>
                                                        {typeof opt.weight === "object" ? opt.weight.weightLabel : opt.weight || "Default"}
                                                        {" — "}₹{opt.discountPrice || opt.originalPrice || 0}
                                                        {opt.originalPrice && opt.discountPrice && opt.originalPrice > opt.discountPrice && ` (MRP: ₹${opt.originalPrice})`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Qty + Price */}
                                    <div className="mt-2.5 flex items-center justify-between">
                                        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                                            <button
                                                onClick={() => updateItemQuantity(idx, item.quantity - 1)}
                                                className="px-2.5 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm font-semibold"
                                            >-</button>
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => updateItemQuantity(idx, e.target.value)}
                                                className="w-10 text-center bg-white dark:bg-gray-700 border-x border-gray-300 dark:border-gray-600 py-1 text-gray-800 dark:text-white outline-none text-sm"
                                            />
                                            <button
                                                onClick={() => updateItemQuantity(idx, item.quantity + 1)}
                                                className="px-2.5 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm font-semibold"
                                            >+</button>
                                        </div>
                                        <span className="text-sm font-bold text-gray-800 dark:text-white">
                                            ₹{((item.selectedOption?.discountPrice || item.selectedOption?.originalPrice || 0) * item.quantity).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Running subtotal */}
                    <div className="flex justify-between items-center py-2 px-1 border-t border-gray-200 dark:border-gray-700 mt-2">
                        <span className="text-sm text-gray-500">Subtotal</span>
                        <span className="font-bold text-gray-800 dark:text-white">
                            ₹{selectedItems.reduce((sum, item) => sum + ((item.selectedOption?.discountPrice || item.selectedOption?.originalPrice || 0) * item.quantity), 0).toFixed(2)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
        // Step 3: Address Entry
    const renderStep3 = () => {
        const inputClass = "w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors placeholder-gray-400";
        return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Delivery Address</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enter the customer's delivery details.</p>
            </div>

            {/* Google Maps search */}
            <div className="relative mb-5">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                    <FaMapMarkerAlt className="inline mr-1.5 text-red-500" size="0.8em" />Search Location
                </label>
                <input
                    ref={addressInputRef}
                    type="text"
                    placeholder="Search for delivery address on Google Maps..."
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                    className="w-full bg-blue-50 dark:bg-gray-700 border-2 border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 text-gray-800 dark:text-white placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-sm"
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-52 overflow-y-auto">
                        {addressSuggestions.map((suggestion, idx) => (
                            <button
                                key={suggestion.place_id || idx}
                                onClick={() => selectAddressSuggestion(suggestion)}
                                className="w-full p-3 text-left hover:bg-blue-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 flex items-start gap-2.5 transition-colors"
                            >
                                <FaMapMarkerAlt className="text-red-500 flex-shrink-0 mt-0.5" size="0.85em" />
                                <span className="text-sm text-gray-800 dark:text-white">{suggestion.description}</span>
                            </button>
                        ))}
                    </div>
                )}
                {address.location && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 flex items-center gap-1">
                        <FaCheckCircle size="0.7em" /> Location coordinates captured — hyperlocal delivery can be checked
                    </p>
                )}
            </div>

            {/* Detailed address fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Customer Name</label>
                    <input type="text" placeholder="Full name" value={address.name} onChange={(e) => setAddress(prev => ({ ...prev, name: e.target.value }))} className={inputClass} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block"><FaPhone className="inline mr-1" size="0.7em" />Mobile <span className="text-red-500">*</span></label>
                    <input
                        type="tel" placeholder="10-digit mobile number" value={address.phone}
                        onChange={(e) => { const val = e.target.value.replace(/\D/g, "").slice(0, 10); setAddress(prev => ({ ...prev, phone: val })); }}
                        maxLength={10} className={inputClass}
                    />
                    {address.phone && address.phone.length > 0 && address.phone.length !== 10 && (
                        <p className="text-xs text-red-500 mt-1">Enter a valid 10-digit number</p>
                    )}
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block"><FaHome className="inline mr-1" size="0.7em" />House / Flat</label>
                    <input type="text" placeholder="House no., Building, Floor" value={address.house} onChange={(e) => setAddress(prev => ({ ...prev, house: e.target.value }))} className={inputClass} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Area / Street</label>
                    <input type="text" placeholder="Street, Area, Colony" value={address.area} onChange={(e) => setAddress(prev => ({ ...prev, area: e.target.value }))} className={inputClass} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Landmark</label>
                    <input type="text" placeholder="Nearby landmark (optional)" value={address.landmark} onChange={(e) => setAddress(prev => ({ ...prev, landmark: e.target.value }))} className={inputClass} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block"><FaCity className="inline mr-1" size="0.7em" />City <span className="text-red-500">*</span></label>
                    <input type="text" placeholder="City" value={address.city} onChange={(e) => setAddress(prev => ({ ...prev, city: e.target.value }))} className={inputClass} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Pincode <span className="text-red-500">*</span></label>
                    <input
                        type="text" placeholder="6-digit pincode" value={address.pincode}
                        onChange={(e) => { const val = e.target.value.replace(/\D/g, "").slice(0, 6); setAddress(prev => ({ ...prev, pincode: val })); }}
                        maxLength={6} className={inputClass}
                    />
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">State</label>
                    <input type="text" placeholder="State" value={address.state} onChange={(e) => setAddress(prev => ({ ...prev, state: e.target.value }))} className={inputClass} />
                </div>
            </div>

            {/* Directions */}
            <div className="mt-3">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">
                    <FaDirections className="inline mr-1" size="0.7em" />Delivery Instructions
                </label>
                <textarea
                    placeholder="Any special directions for the delivery partner..."
                    value={address.directions}
                    onChange={(e) => setAddress(prev => ({ ...prev, directions: e.target.value }))}
                    rows={2}
                    className={`${inputClass} resize-none`}
                />
            </div>
        </div>
        );
    };

    // Step 4: Branch Selection
    const renderStep4 = () => (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Select Branch</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    We'll validate stock availability and deliverability for the selected branch.
                </p>
            </div>

            <div className="space-y-2.5">
                {branches.map(branch => {
                    const isSelected = selectedBranch?._id === branch._id;
                    return (
                        <button
                            key={branch._id}
                            onClick={() => handleBranchSelect(branch)}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                                isSelected
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-800"
                                    : "border-gray-200 dark:border-gray-700 hover:border-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50/50 dark:hover:bg-gray-750"
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-blue-600" : "bg-gray-100 dark:bg-gray-700"}`}>
                                        <FaStore className={isSelected ? "text-white" : "text-gray-400"} size="0.85em" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 dark:text-white text-sm">{branch.branchName}</h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{branch.branchLocation || "No location"} {branch.branchPincode ? `· ${branch.branchPincode}` : ""}</p>
                                    </div>
                                </div>
                                {isSelected && (
                                    <div className="flex-shrink-0">
                                        {validatingBranch ? (
                                            <FaSpinner className="text-blue-500 animate-spin" size="1em" />
                                        ) : branchValidation?.allAvailable ? (
                                            <div className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                                                <FaCheckCircle className="text-emerald-500" size="0.9em" />
                                            </div>
                                        ) : branchValidation ? (
                                            <div className="w-7 h-7 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                                <FaExclamationTriangle className="text-red-500" size="0.85em" />
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Validation Results */}
            {selectedBranch && !validatingBranch && branchValidation && (
                <div className={`mt-4 p-4 rounded-xl border ${
                    branchValidation.allAvailable && branchValidation.isDeliverable !== false
                        ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                        : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                }`}>
                    {branchValidation.allAvailable && branchValidation.isDeliverable !== false ? (
                        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium text-sm">
                            <FaCheckCircle />
                            All items are available at this branch
                        </div>
                    ) : (
                        <div>
                            {branchValidation.deliveryMessage && (
                                <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400 mb-2">
                                    <FaExclamationTriangle className="flex-shrink-0 mt-0.5" size="0.85em" />
                                    <span className="text-sm">{branchValidation.deliveryMessage}</span>
                                </div>
                            )}
                            {branchValidation.unavailableItems?.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1 uppercase tracking-wide">Unavailable:</p>
                                    <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-0.5">
                                        {branchValidation.unavailableItems.map((item, idx) => (
                                            <li key={idx}>{item.productName || ""}{item.reason ? ` — ${item.reason}` : ""}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {branchValidation.availableItems?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-current/20">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Stock Details</p>
                            {branchValidation.availableItems.map((item, idx) => (
                                <div key={idx} className="text-xs text-gray-500 dark:text-gray-400 flex justify-between">
                                    <span>{item.productName}</span>
                                    <span>Available: {item.available} / Required: {item.required}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // Step 5: Delivery & Coupon
    const renderStep5 = () => {
        const financials = calculateFinancials();
        const hasGeoCoords = !!(address.location?.coordinates);
        const isQuick = selectedOrderType === "quick_shiprocket";

        return (
            <div>
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Delivery & Pricing</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Set payment method, delivery option, and apply a discount if needed.</p>
                </div>

                {/* Payment Method Selection */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
                    <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                        <span className="w-7 h-7 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                            <FaMoneyBillWave className="text-emerald-600 dark:text-emerald-400" size="0.8em" />
                        </span>
                        Payment Method
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setPaymentMethod("cod")}
                            className={`p-4 rounded-xl border-2 transition-all text-center ${
                                paymentMethod === "cod"
                                    ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-300"
                                    : "border-gray-200 dark:border-gray-700 hover:border-emerald-300 bg-white dark:bg-gray-800"
                            }`}
                        >
                            <FaMoneyBillWave className={`mx-auto mb-1.5 ${paymentMethod === "cod" ? "text-emerald-600" : "text-gray-400"}`} size="1.3em" />
                            <div className={`text-sm font-semibold ${paymentMethod === "cod" ? "text-emerald-700 dark:text-emerald-400" : "text-gray-600 dark:text-gray-400"}`}>
                                Cash on Delivery
                            </div>
                            {paymentMethod === "cod" && <FaCheck className="text-emerald-500 mx-auto mt-1" size="0.65em" />}
                        </button>
                        <button
                            onClick={() => setPaymentMethod("prepaid")}
                            className={`p-4 rounded-xl border-2 transition-all text-center ${
                                paymentMethod === "prepaid"
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-300"
                                    : "border-gray-200 dark:border-gray-700 hover:border-blue-300 bg-white dark:bg-gray-800"
                            }`}
                        >
                            <FaEnvelope className={`mx-auto mb-1.5 ${paymentMethod === "prepaid" ? "text-blue-600" : "text-gray-400"}`} size="1.3em" />
                            <div className={`text-sm font-semibold ${paymentMethod === "prepaid" ? "text-blue-700 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
                                Online / Prepaid
                            </div>
                            {paymentMethod === "prepaid" && <FaCheck className="text-blue-500 mx-auto mt-1" size="0.65em" />}
                        </button>
                    </div>
                </div>

                {/* Delivery charges section */}
                {selectedOrderType === "regular_online" ? (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
                        <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                            <span className="w-7 h-7 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                <FaTruck className="text-blue-600 dark:text-blue-400" size="0.75em" />
                            </span>
                            Delivery Charges
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Enter the delivery charges (0 for free delivery).</p>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">₹</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={deliveryCharges}
                                onChange={(e) => setDeliveryCharges(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="w-36 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="0.00"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
                        <h3 className="font-semibold text-gray-800 dark:text-white mb-1 flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${isQuick ? "bg-orange-100 dark:bg-orange-900/30" : "bg-purple-100 dark:bg-purple-900/30"}`}>
                                {isQuick ? <FaBolt className="text-orange-600 dark:text-orange-400" size="0.75em" /> : <FaRocket className="text-purple-600 dark:text-purple-400" size="0.75em" />}
                            </span>
                            {isQuick ? "Quick Shiprocket Delivery Options" : "Shiprocket Delivery Options"}
                        </h3>

                        {/* No geo-coords warning for quick order */}
                        {isQuick && !hasGeoCoords && (
                            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                                <FaExclamationTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size="0.9em" />
                                <div>
                                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Address coordinates missing</p>
                                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">For accurate hyperlocal availability, please select a precise address using Google Maps search in step 3.</p>
                                </div>
                            </div>
                        )}

                        <div className="mt-3">
                        {loadingCouriers ? (
                            <div className="flex items-center justify-center py-8 text-gray-400 gap-3">
                                <FaSpinner className="animate-spin" size="1.1em" />
                                <span className="text-sm">{isQuick ? "Checking hyperlocal availability..." : "Loading courier rates..."}</span>
                            </div>
                        ) : isQuick && hyperlocalAvailable === false ? (
                            <div className="py-6 text-center">
                                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <FaWifi className="text-red-400" size="1.2em" />
                                </div>
                                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Hyperlocal delivery not available</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Same-day delivery is not serviceable for this route. Either try prepaid payment or choose a different order type.</p>
                                <div className="flex items-center justify-center gap-3">
                                    <button onClick={fetchCourierRates} className="text-sm text-blue-600 hover:text-blue-700 font-medium underline">
                                        Retry Check
                                    </button>
                                </div>
                            </div>
                        ) : shiprocketCouriers.length === 0 ? (
                            <div className="py-6 text-center">
                                <p className="text-gray-500 dark:text-gray-400 mb-3 text-sm">No courier options available for this route.</p>
                                <button onClick={fetchCourierRates} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {shiprocketCouriers.map((courier, idx) => {
                                    const isSelected = selectedCourier?.courier_company_id === courier.courier_company_id;
                                    const rate = courier.rate || courier.freight_charge || 0;
                                    const modeColor = courier.mode === "Hyperlocal"
                                        ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                        : courier.mode === "Air"
                                        ? "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400"
                                        : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
                                    return (
                                        <button
                                            key={courier.courier_company_id || idx}
                                            onClick={() => setSelectedCourier(courier)}
                                            className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${
                                                isSelected
                                                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 ring-2 ring-indigo-200 dark:ring-indigo-800"
                                                    : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 bg-white dark:bg-gray-800/80"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-semibold text-gray-800 dark:text-white text-sm">{courier.courier_name}</span>
                                                        {courier.mode && (
                                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${modeColor}`}>{courier.mode}</span>
                                                        )}
                                                    </div>
                                                    {courier.etd && (
                                                        <span className="text-xs text-gray-400 mt-0.5 block">ETD: {courier.etd}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className="font-bold text-gray-800 dark:text-white">₹{rate}</span>
                                                    {isSelected && <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center"><FaCheck className="text-white" size="0.55em" /></div>}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        </div>
                    </div>
                )}

                {/* Custom Coupon Amount */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-4">
                    <h3 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                        <span className="w-7 h-7 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <FaPercent className="text-green-600 dark:text-green-400" size="0.75em" />
                        </span>
                        Custom Coupon Discount
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Apply a custom discount amount (no conditions applied).
                    </p>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-700">
                            <span className="px-3 py-2.5 text-gray-500 text-sm border-r border-gray-300 dark:border-gray-600">₹</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={customCouponAmount}
                                onChange={(e) => setCustomCouponAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="w-28 bg-transparent px-3 py-2.5 text-gray-800 dark:text-white outline-none text-sm"
                                placeholder="0.00"
                            />
                        </div>
                        {!couponApplied && customCouponAmount > 0 && (
                            <button onClick={() => setCouponApplied(true)} className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
                                Apply
                            </button>
                        )}
                        {couponApplied && (
                            <button onClick={() => { setCouponApplied(false); setCustomCouponAmount(0); }} className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg text-sm font-medium transition-colors dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                                Remove
                            </button>
                        )}
                    </div>
                    {couponApplied && customCouponAmount > 0 && (
                        <div className="mt-2.5 flex items-center gap-1.5 text-green-600 dark:text-green-400">
                            <FaCheckCircle size="0.8em" />
                            <span className="text-sm font-medium">₹{customCouponAmount.toFixed(2)} discount applied</span>
                        </div>
                    )}
                </div>

                {/* Financial Summary */}
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800/80 dark:to-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                        <FaShoppingCart size="0.85em" className="text-blue-500" /> Order Summary
                    </h3>
                    <div className="space-y-2.5 text-sm">
                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                            <span>Subtotal ({selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""})</span>
                            <span className="font-medium">₹{financials.subtotal.toFixed(2)}</span>
                        </div>
                        {financials.totalDiscount > 0 && (
                            <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                                <span>Product Discount</span>
                                <span className="font-medium">-₹{financials.totalDiscount.toFixed(2)}</span>
                            </div>
                        )}
                        {financials.couponDiscount > 0 && (
                            <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                                <span>Coupon Discount</span>
                                <span className="font-medium">-₹{financials.couponDiscount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                            <span>Delivery Charges</span>
                            <span className={`font-medium ${financials.deliveryCharges === 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
                                {financials.deliveryCharges > 0 ? `₹${financials.deliveryCharges.toFixed(2)}` : "Free"}
                            </span>
                        </div>
                        <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-1">
                            <div className="flex justify-between font-bold text-gray-800 dark:text-white text-base">
                                <span>Total Amount</span>
                                <span>₹{financials.finalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Step 6: Review & Create
    const renderStep6 = () => {
        const financials = calculateFinancials();
        const orderTypeInfo = ORDER_TYPES.find(t => t.id === selectedOrderType);

        return (
            <div>
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Review & Create</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Please review the order details before creating.</p>
                </div>

                <div className="space-y-3">
                    {/* Order Type */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Order Type</p>
                        <div className="flex items-center gap-2 text-gray-800 dark:text-white font-semibold">
                            {orderTypeInfo?.icon}
                            <span>{orderTypeInfo?.label}</span>
                        </div>
                    </div>

                    {/* Items */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Products ({selectedItems.length})</p>
                        <div className="space-y-2.5">
                            {selectedItems.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                    <ProductImage src={getProductImageUrl(item.product)} alt={item.product.productName} className="w-10 h-10 rounded-lg" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-800 dark:text-white truncate">{item.product.productName}</div>
                                        <div className="text-xs text-gray-500">
                                            Qty: {item.quantity}
                                            {item.selectedOption && ` · ${typeof item.selectedOption.weight === "object" ? item.selectedOption.weight.weightLabel : item.selectedOption.weight || "Default"}`}
                                        </div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-800 dark:text-white flex-shrink-0">
                                        ₹{((item.selectedOption?.discountPrice || item.selectedOption?.originalPrice || 0) * item.quantity).toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Address */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Delivery Address</p>
                        <div className="text-sm text-gray-800 dark:text-white space-y-0.5">
                            {address.name && <div className="font-semibold">{address.name}</div>}
                            {address.phone && <div className="text-gray-600 dark:text-gray-400 flex items-center gap-1"><FaPhone size="0.7em" />{address.phone}</div>}
                            {/* Line 1: house / flat */}
                            {address.house && <div className="text-gray-700 dark:text-gray-300 font-medium">{address.house}</div>}
                            {/* Line 2: full Google geocoded address (Search Location string) */}
                            {address.address && <div className="text-gray-600 dark:text-gray-400">{address.address}</div>}
                            {/* Line 3: city, state, pincode */}
                            <div className="text-gray-600 dark:text-gray-400">
                                {[address.city, address.state, address.pincode].filter(Boolean).join(", ")}
                            </div>
                            {address.directions && <div className="text-xs text-gray-500 italic mt-1">📍 {address.directions}</div>}
                        </div>
                    </div>

                    {/* Branch & Payment row */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Branch</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-white">{selectedBranch?.branchName}</p>
                            <p className="text-xs text-gray-500">{selectedBranch?.branchLocation || selectedBranch?.branchPincode}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Payment</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-white">
                                {paymentMethod === "cod" ? "Cash on Delivery" : "Online / Prepaid"}
                            </p>
                        </div>
                    </div>

                    {/* Delivery Partner */}
                    {selectedCourier && (
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Delivery Partner</p>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{selectedCourier.courier_name}</p>
                                    {selectedCourier.etd && <p className="text-xs text-gray-500">ETD: {selectedCourier.etd}</p>}
                                </div>
                                <span className="text-base font-bold text-gray-800 dark:text-white">₹{selectedCourier.rate || selectedCourier.freight_charge || 0}</span>
                            </div>
                        </div>
                    )}

                    {/* Financial Summary */}
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 text-white">
                        <p className="text-sm font-semibold opacity-80 mb-3">Payment Summary</p>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between opacity-90"><span>Subtotal</span><span>₹{financials.subtotal.toFixed(2)}</span></div>
                            {financials.couponDiscount > 0 && (
                                <div className="flex justify-between text-green-300"><span>Discount</span><span>-₹{financials.couponDiscount.toFixed(2)}</span></div>
                            )}
                            <div className="flex justify-between opacity-90"><span>Delivery</span><span>{financials.deliveryCharges > 0 ? `₹${financials.deliveryCharges.toFixed(2)}` : "Free"}</span></div>
                            <div className="border-t border-white/30 pt-2 mt-2 flex justify-between font-bold text-lg">
                                <span>Total</span><span>₹{financials.finalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create button */}
                <button
                    onClick={handleCreateOrder}
                    disabled={creatingOrder}
                    className="w-full mt-6 py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-bold text-base transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                    {creatingOrder ? (
                        <><FaSpinner className="animate-spin" /> Creating Order...</>
                    ) : (
                        <><FaCheckCircle /> Create {orderTypeInfo?.label}</>
                    )}
                </button>
            </div>
        );
    };

    // Main render with wizard
    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            case 5: return renderStep5();
            case 6: return renderStep6();
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900 p-4 md:p-6">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-5">
                    <button
                        onClick={() => {
                            if (currentStep === 1) {
                                setShowWizard(false);
                                setCurrentStep(0);
                            } else {
                                goBack();
                            }
                        }}
                        className="p-2.5 rounded-xl hover:bg-white dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                    >
                        <FaArrowLeft size="0.9em" />
                    </button>
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">Create Custom Order</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Step {currentStep} of {STEPS.length}</p>
                    </div>
                </div>

                {/* Stepper */}
                {renderStepper()}

                {/* Step Content */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 md:p-6 mb-4">
                    {renderCurrentStep()}
                </div>

                {/* Navigation */}
                {currentStep < 6 && (
                    <div className="flex justify-between items-center">
                        <button
                            onClick={goBack}
                            disabled={currentStep === 1}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 border border-transparent hover:border-gray-200 dark:hover:border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
                        >
                            <FaArrowLeft size="0.8em" /> Back
                        </button>
                        <button
                            onClick={goNext}
                            disabled={!canProceed()}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white disabled:text-gray-500 dark:disabled:text-gray-400 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
                        >
                            Next <FaArrowRight size="0.8em" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateCustomOrder;
